import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import ConfirmDialog, { ConfirmTone } from '../components/ConfirmDialog';

// Promise-based confirmation hook so any component can call:
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Delete?', message: '...' })) { ... }
// instead of falling back to window.confirm. Matches the styling of every
// other modal in the app.

export interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Where focus goes when the dialog closes (see ConfirmDialog). */
  returnFocusTo?: React.RefObject<HTMLElement | null> | string | null;
}

type Resolver = (value: boolean) => void;

interface State extends ConfirmOptions {
  resolve: Resolver;
}

const Ctx = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pending, setPending] = useState<State | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const handleCancel = () => {
    if (pending) {
      pending.resolve(false);
      setPending(null);
    }
  };

  const handleConfirm = () => {
    if (pending) {
      pending.resolve(true);
      setPending(null);
    }
  };

  const value = useMemo(() => confirm, [confirm]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!pending}
        title={pending?.title ?? ''}
        message={pending?.message}
        confirmLabel={pending?.confirmLabel}
        cancelLabel={pending?.cancelLabel}
        tone={pending?.tone}
        returnFocusTo={pending?.returnFocusTo}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </Ctx.Provider>
  );
};

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
